import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import FrameworkNode from 'models/framework-node.model';
import { ImpactTextEnum, QuestionImpactsEnum } from './enums/impact.enum';
import {
  AnswerEnum,
  AnswerEnumExtended,
  BnbAnswerEnum,
  FRAMEWORKS_ANSWERS,
  ALLOWED_ANSWERS,
} from './enums/answer.enum';
import { SortDirectionEnum } from './enums/sort-direction.enum';
import { parseDomain, fromUrl } from 'parse-domain';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { ProbabilityEnum, ImpactEnum, RiskMapEnum } from 'app/shared/enums/risk-map.enums';
import { NA_SCORES } from 'app/collection/collection.constant';
import { NOT_APPLICABLE_ARRAY } from './enums/common.enum';

type AnswerObject = {
  [key: string]: {
    name: string;
    score: number;
    comment: boolean;
    artifact: boolean;
    questionOptionOrder: number;
  };
};

import {
  RoleEnum,
  GetEntityQuery,
  ImpactEnum as APIImpactEnum,
  RiskProbabilityEnum,
  RiskImpactEnum,
} from 'app/API.service';
import validator from 'validator';

@Injectable()
export class UtilsService {
  static get isSHC(): boolean {
    if (window.location.origin.includes('localhost')) {
      return false;
    }
    const { subDomains } = parseDomain(fromUrl(window.location.origin)) as any;

    // https://shccares.cegrc.com/
    return subDomains.includes('shccares');
  }

  static get isNatixis(): boolean {
    if (window.location.origin.includes('localhost')) {
      return false;
    }
    const { subDomains } = parseDomain(fromUrl(window.location.origin)) as any;

    // https://natixis.cegrc.com
    return subDomains.includes('natixis');
  }

  static get isHyperclear(): boolean {
    if (window.location.origin.includes('localhost')) {
      return false;
    }
    const { subDomains } = parseDomain(fromUrl(window.location.origin)) as any;

    // https://hyperclear.cygovdev.com | hyperclear.cermp.com
    return subDomains.includes('hyperclear');
  }

  static get isDBU(): boolean {
    if (window.location.origin.includes('localhost')) {
      return false;
    }
    const { subDomains } = parseDomain(fromUrl(window.location.origin)) as any;

    // https://dbu.cegrc.com/
    return subDomains.includes('dbu');
  }

  static get isMoelis(): boolean {
    if (window.location.origin.includes('localhost')) {
      return false;
    }
    const { subDomains } = parseDomain(fromUrl(window.location.origin)) as any;

    // moelis.cygovdev.com, moelis.cegrc.com
    return subDomains.includes('moelis');
  }

  static get isAdnoc(): boolean {
    if (window.location.origin.includes('localhost')) {
      return false;
    }
    const { subDomains } = parseDomain(fromUrl(window.location.origin)) as any;

    // adnoc.cygovdev.com, adnoc.cegrc.com
    return subDomains.includes('adnoc');
  }

  static get isInstar(): boolean {
    if (window.location.origin.includes('localhost')) {
      return false;
    }
    const { subDomains } = parseDomain(fromUrl(window.location.origin)) as any;

    // instar.cegrc.com
    return subDomains.includes('instar');
  }

  static get isJTGlobal(): boolean {
    if (window.location.origin.includes('localhost')) {
      return false;
    }
    const { subDomains } = parseDomain(fromUrl(window.location.origin)) as any;

    // jtglobal.cermp.com OR jtglobal.cygovdev.com
    return subDomains.includes('jtglobal');
  }

  static get isCyberArk(): boolean {
    if (window.location.origin.includes('localhost')) {
      return false;
    }
    const { domain, subDomains } = parseDomain(fromUrl(window.location.origin)) as any;

    // cyberark.cygovdev.com, ca.cegrc.com, ca-test.cermp.com
    return (
      (subDomains.includes('ca') && domain === 'cegrc') ||
      subDomains.includes('cyberark') ||
      subDomains.includes('ca-test')
    );
  }

  static get isENBD(): boolean {
    if (window.location.origin.includes('localhost')) {
      return false;
    }
    const { domain, subDomains } = parseDomain(fromUrl(window.location.origin)) as any;

    // daman.enbdcscm.com, emiratesnbd.cermp.com, emiratesnbd.cygovdev.com
    return (subDomains.includes('daman') && domain === 'enbdcscm') || subDomains.includes('emiratesnbd');
  }

  static get isOklahomaUniversity(): boolean {
    if (window.location.origin.includes('localhost')) {
      return false;
    }
    const { subDomains } = parseDomain(fromUrl(window.location.origin)) as any;

    // ou.cermp.com
    return subDomains.includes('ouhsc') || subDomains.includes('ou');
  }

  static get isSSMHealth(): boolean {
    if (window.location.origin.includes('localhost')) {
      return false;
    }
    const { subDomains } = parseDomain(fromUrl(window.location.origin)) as any;

    // ssmhealth.cygovdev.com, ssmhealth.cermp.com
    return subDomains.includes('ssmhealth');
  }

  static get isWssu(): boolean {
    if (window.location.origin.includes('localhost')) {
      return false;
    }
    const { subDomains } = parseDomain(fromUrl(window.location.origin)) as any;

    return subDomains.includes('wssu');
  }

  static get isHowden(): boolean {
    if (window.location.origin.includes('localhost')) {
      return false;
    }
    const { subDomains } = parseDomain(fromUrl(window.location.origin)) as any;

    // howden.cygovdev.com, howden.cermp.com
    return subDomains.includes('howden');
  }

  static get isInsuranceApp(): boolean {
    if (window.location.origin.includes('localhost')) {
      return false;
    }
    const { subDomains } = parseDomain(fromUrl(window.location.origin)) as any;

    // insurance.cegrc.com
    return subDomains.includes('insurance');
  }

  static get isBnBCyberSite(): boolean {
    // isBeecher
    if (window.location.origin.includes('localhost')) {
      return false;
    }
    const { subDomains } = parseDomain(fromUrl(window.location.origin)) as any;

    // cyber-in-site.cygovdev.com, cyber-in-site.bbrown.com, test-cyber-in-site.bbrown.com, insurance.cegrc.com
    return (
      subDomains.includes('test-cyber-in-site') ||
      subDomains.includes('cyber-in-site') ||
      subDomains.includes('insurance')
    );
  }

  static get isInformatica(): boolean {
    // isInformatica
    if (window.location.origin.includes('localhost')) {
      return false;
    }
    const { subDomains } = parseDomain(fromUrl(window.location.origin)) as any;

    // informatica.cegrc.com, informatica.cygovdev.com
    return subDomains.includes('informatica');
  }

  static get isInformaticaUat(): boolean {
    // isInformaticaUat
    if (window.location.origin.includes('localhost')) {
      return false;
    }
    const { subDomains } = parseDomain(fromUrl(window.location.origin)) as any;

    // informatica-uat.cegrc.com
    return subDomains.includes('informatica-uat');
  }

  static get isBnB(): boolean {
    if (window.location.origin.includes('localhost')) {
      return false;
    }
    const { domain, subDomains } = parseDomain(fromUrl(window.location.origin)) as any;
    // cyber-test.bbins.com, cyber.bbins.com, bbins.cygovdev.com, bbins2.cygovdev.com
    return domain === 'bbins' || subDomains.includes('bbins') || subDomains.includes('bbins2');
  }

  static get isMidMarket(): boolean {
    if (window.location.origin.includes('localhost')) {
      return false;
    }
    const { subDomains } = parseDomain(fromUrl(window.location.origin)) as any;

    // midmarket.cegrc.com OR midmarket.cygovdev.com OR cyberapp.bbrown.com (prod)
    return subDomains.includes('midmarket') || subDomains.includes('cyberapp');
  }

  static get isCRB(): boolean {
    if (window.location.origin.includes('localhost')) {
      return false;
    }
    const { subDomains } = parseDomain(fromUrl(window.location.origin)) as any;

    // crb.cygovdev.com, crossriver.cermp.com
    return subDomains.length ? subDomains.includes('crb') || subDomains.includes('crossriver') : false;
  }

  static get isCNM(): boolean {
    if (window.location.origin.includes('localhost')) {
      return false;
    }
    const { subDomains } = parseDomain(fromUrl(window.location.origin)) as any;

    // cnm.cermp.com
    return subDomains.length ? subDomains.includes('cnm') : false;
  }

  static get isNetskope(): boolean {
    // Change it to FALSE for normal app flow and TRUE for Netskope feature Mode.
    if (window.location.origin.includes('localhost')) {
      return false;
    }
    const { subDomains } = parseDomain(fromUrl(window.location.origin)) as any;

    // netskope.cygovdev.com, netskope.cygovrmp.com
    return subDomains.length ? subDomains.includes('netskope') : false;
  }

  static get isITC(): boolean {
    // Change it to FALSE for normal app flow and TRUE for Netskope feature Mode.
    if (window.location.origin.includes('localhost')) {
      return false;
    }
    const { subDomains, domain } = parseDomain(fromUrl(window.location.origin)) as any;

    // itc.cygovdev.com, live.navigatoritcsecure.com, dev.navigatordevitcsecure.com
    return (
      domain === 'navigatordevitcsecure' ||
      domain === 'navigatoritcsecure' ||
      subDomains.includes('itc-demo') ||
      (domain === 'cygovdev' && subDomains.includes('itc'))
    );
  }

  /**
   * Converts between AWSDateTime (ISO 8601 string) and timestamp.
   * - If input is a timestamp (number), returns AWSDateTime (string)
   * - If input is an AWSDateTime (string), returns timestamp (number)
   *
   * @param {string|number} value - The input value to convert
   * @returns {string|number|null} - Converted value or null if invalid
   */
  static convertDateTime(value) {
    // Case 1: value is a timestamp (number or numeric string)
    if (typeof value === 'number' || (!isNaN(value) && value.trim() !== '')) {
      const numValue = Number(value);
      // Check if timestamp is in seconds — convert to milliseconds if needed
      const date = new Date(numValue < 1e12 ? numValue * 1000 : numValue);
      return date.toISOString(); // AWSDateTime format
    }

    // Case 2: value is an AWSDateTime string
    if (typeof value === 'string') {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date.getTime(); // timestamp in milliseconds
      }
    }

    // Case 3: Invalid value
    console.warn('Invalid date/time value provided:', value);
    return null;
  }

  static get isLiveDomain(): boolean {
    if (window.location.origin.includes('localhost')) {
      return false;
    }
    const { subDomains } = parseDomain(fromUrl(window.location.origin)) as any;

    return subDomains.includes('live') || subDomains.includes('test');
  }

  static get isDevDomain(): boolean {
    if (window.location.origin.includes('localhost')) {
      return true;
    }
    const { domain } = parseDomain(fromUrl(window.location.origin)) as any;

    return domain === 'cygovdev';
  }

  static get isQA(): boolean {
    if (window.location.origin.includes('localhost')) {
      return false;
    }
    return false;
    // return 'dev.cygovdev.com' === 'qa.cygovdev.com';
  }

  static get companyConfig(): any {
    const defaultData = {
      title: 'Centraleyes',
      ogTitle: 'Centraleyes',
      ogDescription: 'Centraleyes | Risk Management Reimagined',
      ogImage: './assets/centraleyes-logo.png',
      favicon: './assets/images/favicon-310.png',
      mainLogoName: 'centraleyes-logo',
    };

    const BnBData = { ...defaultData, mainLogoName: 'ce-part-logo' };
    const BnBinSiteData = { ...defaultData, mainLogoName: 'cyber-in-site-logo' };
    const howdenData = { ...defaultData, mainLogoName: 'howden-logo' };
    const SSMHealthData = { ...defaultData, mainLogoName: 'SSM-Health' };
    const instarData = { ...defaultData, mainLogoName: 'instar-logo' };
    const midMarketData = { ...defaultData, title: 'Cyber app', mainLogoName: 'cyber-in-site-logo' };
    const natixisData = { ...defaultData, mainLogoName: 'natixis-logo' };

    const ITCData = {
      title: 'ITC | Navigator',
      ogTitle: 'ITC | Navigator',
      ogDescription: 'ITC | Navigator',
      ogImage: './assets/images/ITC_logo.png',
      favicon: './assets/images/ITC_logo.png',
      mainLogoName: 'ITC_logo',
    };

    switch (true) {
      case UtilsService.isITC:
        return ITCData;
      case UtilsService.isBnB:
        return BnBData;
      case UtilsService.isBnBCyberSite && !UtilsService.isInsuranceApp:
        return BnBinSiteData;
      case UtilsService.isMidMarket:
        return midMarketData;
      case UtilsService.isHowden:
        return howdenData;
      case UtilsService.isSSMHealth:
        return SSMHealthData;
      case UtilsService.isInstar:
        return instarData;
      case UtilsService.isNatixis:
        return natixisData;
      default:
        return defaultData;
    }
  }

  static get isUSBank(): boolean {
    if (window.location.origin.includes('localhost')) {
      return false;
    }
    const { subDomains } = parseDomain(fromUrl(window.location.origin)) as any;
    // https://usb.cermp.com/
    return subDomains.includes('usb');
  }

  static getStringEnum(enumnum) {
    const fields = [];
    Object.keys(enumnum).forEach(key => {
      if (key !== key.toUpperCase()) {
        fields.push(key);
      }
    });
    return fields;
  }

  // help function to get an array instead of tree
  static getArrayFromNestedObject(object: any) {
    const arr = [];
    const traverseSurvey = sheet => {
      Object.keys(sheet).forEach(key => {
        if (sheet[key].answers) {
          arr.push(sheet[key]);
        } else {
          traverseSurvey(sheet[key]);
        }
      });
    };

    traverseSurvey(object);
    return arr;
  }

  static isHebrew(str): boolean {
    return str.match(/[\u0590-\u05FF]+/g);
  }

  static capitalizeFirstLetter(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  static isIdFake(id: string): boolean {
    return id.length === 37 ? true : false;
  }

  static isDefined(variable: any): boolean {
    return !(typeof variable === 'undefined' || variable === null);
  }

  static isEmpty(variable: string): boolean {
    return !this.isDefined(variable) || variable === '' || variable === 'null';
  }

  static averageOfProp<T>(arr: T[], prop: string): number {
    const filteredArr = arr.filter(item => item[prop] !== undefined && typeof item[prop] === 'number');
    const sum = filteredArr.reduce((acc, curr) => acc + curr[prop], 0);
    return arr.length > 0 ? sum / arr.length : 0;
  }
  /**
   *
   * @param email string type email address.
   * @returns true if email is in valid format.
   */
  static isValidEmail(email: string): boolean {
    const regexForEmail = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const isValidEmail = regexForEmail.test(email);
    return isValidEmail;
  }

  static isTodayForAnyTimezone(input: any): boolean {
    let date;

    if (typeof input === 'number') {
      if (input < 1e12) {
        input *= 1000;
      }
      date = new Date(input);
    } else if (typeof input === 'string') {
      date = new Date(input);
    } else {
      return false;
    }

    if (isNaN(date)) return false;

    const today = new Date();

    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  }

  /**
   * Returns a random number between min (inclusive) and max (exclusive)
   */
  static getRandomNumber(min: number, max: number, toFixed: number): number {
    let randomNum: any = Math.random() * (max - min) + min;
    if (!isNaN(toFixed)) {
      randomNum = randomNum.toFixed(toFixed);
    }
    return Number(randomNum);
  }

  static getRandomElement(arr: any[]) {
    const index = UtilsService.getRandomNumber(0, arr.length - 1, 0);
    return arr[index];
  }

  static getRandomBoolean() {
    return UtilsService.getRandomElement([true, false]);
  }

  /**
   * Sort array by property.
   * @param array
   * @param prop: string
   * @return sorted array
   */
  static sortByProp<T>(arr: T[], prop: string, direction: SortDirectionEnum = SortDirectionEnum.ASCENDING): T[] {
    return arr?.slice()?.sort((a, b) => {
      if (direction === SortDirectionEnum.ASCENDING) {
        return a[prop] < b[prop] ? -1 : 1;
      } else {
        return a[prop] < b[prop] ? 1 : -1;
      }
    });
  }
  /**
   * Sort array by date property.
   * @param array
   * @param prop: string
   * @return sorted array
   */
  static sortByDate<T>(arr: T[], prop: string, direction: SortDirectionEnum = SortDirectionEnum.ASCENDING): T[] {
    return arr.sort((a, b) => {
      if (direction === SortDirectionEnum.ASCENDING) {
        return new Date(a[prop]).getTime() - new Date(b[prop]).getTime();
      } else {
        return new Date(b[prop]).getTime() - new Date(a[prop]).getTime();
      }
    });
  }

  /**
   * Group array to object by property.
   * @param array
   * @param prop: string
   * @return grouped object
   */
  static groupBy<T>(arr: T[], prop: string): Map<string, T[]> {
    const initialMap: Map<string, T[]> = new Map();
    return arr.reduce((memo, x) => {
      if (!memo[x[prop]]) {
        memo[x[prop]] = [];
      }
      memo[x[prop]].push(x);
      return memo;
    }, initialMap);
  }

  /**
   * Create a map of all items of array with an attribute as key
   *
   * @param arr array
   * @param prop attribute
   */
  static mapBy<T>(arr: T[], prop: string): Map<string, T> {
    const initialMap: Map<string, T> = new Map();
    return arr.reduce((map, curr) => {
      map[curr[prop]] = curr;
      return map;
    }, initialMap);
  }

  static copyArrayDeep<T>(arr: T[]): T[] {
    return JSON.parse(JSON.stringify(arr));
  }

  static getUniqueListBy<T>(arr: T[], key: string): T[] {
    return [...new Map(arr.map(item => [item[key].toLowerCase(), item])).values()];
  }

  /**
   * generate the object from entries
   * Temporary until Microsoft doesn't support ES2019 in TS
   * The source code: http://2ality.com/2019/01/object-from-entries.html
   * @param iterable list of pairs key-value
   */
  static fromEntries(iterable): { [propName: string]: string } {
    const result = {};
    for (const [key, value] of iterable) {
      let coercedKey;
      if (typeof key === 'string' || typeof key === 'symbol') {
        coercedKey = key;
      } else {
        coercedKey = String(key);
      }
      Object.defineProperty(result, coercedKey, {
        value,
        writable: true,
        enumerable: true,
        configurable: true,
      });
    }
    return result;
  }

  static stopBubbling(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  static getRouteParam(child: ActivatedRouteSnapshot, key: string) {
    if (child) {
      if (child.params[key]) {
        return child.params[key];
      } else if (child.queryParams[key]) {
        return child.queryParams[key];
      } else if (child.fragment) {
        // parse url starting from #
        const splitFragment = child.fragment.split(key);
        if (splitFragment && splitFragment.length >= 2) {
          return splitFragment[1].substring(1);
        }
        return null;
      } else {
        return this.getRouteParam(child.firstChild, key);
      }
    } else {
      return null;
    }
  }

  static msgFromError(e): string {
    let message = 'Internal Server Error';
    if (typeof e === 'string') {
      message = e;
    } else if (e.errors) {
      message = `Errors: \n ${e.errors.map(error => error.message).join('\n')}`;
    } else if (e.status === 0) {
      message = 'Server down. Please connect support.';
    } else if (e.message.indexOf('dialCode') !== -1) {
      message = 'Enter valid country code';
    } else if (!e.message.includes('Internal Server Error')) {
      message = 'Invalid credentials. Kindly login again!';
    }
    return message;
  }

  static getShortDate(date: Date, isUTC = false) {
    const shortDate = isUTC
      ? date.getUTCFullYear() + '-' + (date.getUTCMonth() + 1) + '-' + date.getUTCDate()
      : date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate();
    return shortDate;
  }

  static convertDate(date: Date) {
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return (
      (month < 10 ? '0' + month : month.toString()) +
      '/' +
      (day < 10 ? '0' + day : day.toString()) +
      '/' +
      (year < 10 ? '0' + year : year.toString())
    );
  }

  //* converts to DD/MM/YY
  static convertToCustomDateSlashFormat(isoDate: string): string {
    const date = new Date(isoDate);

    // Get day, month, and year
    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // Months are zero-based
    const year = date.getUTCFullYear();

    // Format as DD/MM/YYYY
    return `${day}/${month}/${year}`;
  }

  static sum(array, key): number {
    return array.reduce((a, b) => a + (b[key] || 0), 0);
  }

  static filterOutNullElements = node => node != null;

  static filterOutUndefinedElements = node => node !== undefined;

  static calculateNodesScoreSum = (prev, current) => {
    if (prev && current) {
      prev.key = current.key;
      prev.name = current.name;
      prev.scores.total += current.scores?.total ? current.scores.total : 0;
      prev.scores.target += current.scores.target ? current.scores.target : 0;
      prev.scores.collection += current.scores.collection ? current.scores.collection : 0;
      return prev;
    }
  };

  static buildFlatList = (prev, current) => {
    return prev.concat(...current);
  };

  static buildUniqueNamesList = (prev, current) => {
    return prev.indexOf(current?.name) === -1 ? prev.concat(current?.name) : prev;
  };

  static buildFrameworksList = async (frameworks: any[]): Promise<FrameworkNode[]> => {
    let frameworkList: FrameworkNode[] = [];
    if (frameworks && frameworks.length > 0) {
      // Create list for all unique keys of sub nodes i.e identity, recover
      const frameworkNames = frameworks.reduce(UtilsService.buildUniqueNamesList, []);

      // From the flat array of sub nodes, calculate average for scores
      const promises = frameworkNames.map(calculateFrameworkAverageScore, {
        frameworkList: frameworks,
      });
      frameworkList = await Promise.all(promises);
    }
    return frameworkList;
  };

  static detailedAnswerNumToEnum(value: number): string {
    if (!this.isDefined(value) || value === -1) {
      return 'Not Specified';
    }
    if ((value === 0 && (this.isMidMarket || this.isBnBCyberSite)) || value === 999) {
      return AnswerEnum.NOT_APPLICABLE;
    } else if (value > 9) {
      return AnswerEnum.YES;
    } else if (value > 7.5) {
      return `${AnswerEnum.PARTIAL} 9`;
    } else if (value > 5) {
      return `${AnswerEnum.PARTIAL} 7.5`;
    } else if (value > 2.5) {
      return `${AnswerEnum.PARTIAL} 5`;
    } else if (value > 1) {
      return `${AnswerEnum.PARTIAL} 2.5`;
    } else {
      return AnswerEnum.NO;
    }
  }

  static answerNumToEnum(value: number): AnswerEnum {
    if (!this.isDefined(value) || value === -1) {
      return null;
    }
    if ((value === 0 && (this.isMidMarket || this.isBnBCyberSite)) || value === 999) {
      return AnswerEnum.NOT_APPLICABLE;
    } else if (value > 9) {
      return AnswerEnum.YES;
    } else if (value > 1 && value <= 9) {
      return AnswerEnum.PARTIAL;
    } else {
      return AnswerEnum.NO;
    }
  }

  //* Function to check if the answer is Not Applicable type
  static isNotApplicableAnswer(answerName: string): boolean {
    if (!this.isDefined(answerName)) {
      return false;
    }
    return NOT_APPLICABLE_ARRAY.some(variant => variant.toLowerCase() === answerName?.toLowerCase());
  }

  //* Function to check if the name is matching with the filter name
  //* used in filters
  static isNameMatchingFilter(value: string, filterName: string): boolean {
    if (typeof value === 'string') {
      return value.toLowerCase().split(' ').join('_') === filterName.toLowerCase().split(' ').join('_');
    }
    return false;
  }

  //* Function Used for custom scores
  static getScoreOrKeyForCustomScore(value: number | string, customScoresArray) {
    if (typeof value === 'string') {
      return customScoresArray.find(score => score.answerName.toLowerCase() === value.toLowerCase())?.answerScore;
    } else if (typeof value === 'number') {
      return customScoresArray.find(score => score.answerScore === value)?.name;
    }
    return null;
  }

  static getScoreOrKey(value, objectData, partialScore = null) {
    if (typeof partialScore !== null && typeof value === 'string' && value.toUpperCase().includes(AnswerEnum.PARTIAL)) {
      switch (value) {
        case AnswerEnumExtended.PARTIAL_25:
        case AnswerEnumExtended.PARTIAL_50:
        case AnswerEnumExtended.PARTIAL_75:
        case AnswerEnumExtended.PARTIAL_90:
          return objectData?.PARTIAL?.extended?.[value]?.score;
        default:
          return partialScore;
      }
    }

    const keys = Object.keys(objectData);
    if (objectData[value]) {
      return objectData[value].score;
    }
    const foundByName = keys.find(
      key =>
        typeof value !== 'number' &&
        objectData[key].name.toUpperCase().split(' ').join('_') === value?.toUpperCase().split(' ').join('_')
    );
    if (foundByName) {
      return objectData[foundByName].score;
    }

    if (typeof value === 'number') {
      const foundByScore = keys.find(key => objectData[key].score === UtilsService.answerValueToStandardValue(value));
      if (foundByScore) {
        return objectData[foundByScore].name;
      }

      if (objectData.PARTIAL && objectData.PARTIAL.extended) {
        const partialKeys = Object.keys(objectData.PARTIAL.extended);
        const foundByPartialScore = partialKeys.find(
          key => objectData.PARTIAL.extended[key].score === UtilsService.answerValueToStandardValue(value)
        );
        if (foundByPartialScore) {
          return 'PARTIAL';
        }
      }
    }

    return null;
  }

  /**
   * Convert Answer's average value to standard values
   * In case of single question we get the average of answers.
   * Assumption: For value greater than 1 and less than 2.5 we are considering it as 2.5.
   * @param value average answers value
   * @return {number} standarded answers value
   */
  static answerValueToStandardValue(value: number): number {
    if (value > 0 && value <= 1) {
      return 1;
    } else if (value > 1 && value < 4) {
      return 2.5;
    } else if (value >= 4 && value < 6) {
      return 5;
    } else if (value >= 6 && value < 9) {
      return 7.5;
    } else if (value >= 9 && value < 10) {
      return 9;
    } else if (value >= 10 && value < 999) {
      return 10;
    } else {
      return value;
    }
  }

  static answerNumToEnumNew(value: number): AnswerEnum {
    if (!this.isDefined(value) || value === -1) {
      return null;
    }
    if ((value === 0 && (this.isMidMarket || this.isBnBCyberSite)) || value === 999) {
      return AnswerEnum.NOT_APPLICABLE;
    } else if (value > 9) {
      return AnswerEnum.YES;
    } else if (value > 1 && value <= 9) {
      return AnswerEnum.PARTIAL;
    } else {
      return AnswerEnum.NO;
    }
  }

  /**
   * Function to get answers in from of options for BNB case
   * @param value - score
   * @returns - The option against given value
   */
  static bnbAnswerNumToEnum(value: number): BnbAnswerEnum {
    if (!this.isDefined(value) || value === -1) {
      return null;
    }
    if (value >= 0 && value <= 1) {
      return BnbAnswerEnum.NO;
    } else if (value === 2 || value === 2.5 || value === 5 || value === 7.5 || value === 9) {
      return BnbAnswerEnum.PARTIAL;
    } else if (value === 4) {
      return BnbAnswerEnum.YES_NOT_DOCUMENTED;
    } else if (value === 6) {
      return BnbAnswerEnum.YES_DOCUMENTED_1;
    } else if (value === 8) {
      return BnbAnswerEnum.YES_DOCUMENTED_2;
    } else if (value === 10) {
      return BnbAnswerEnum.YES_DOCUMENTED_3;
    }
  }

  /**
   * Function to map old and new BNB answers
   * @param value = selected option
   * @returns - possible score against that option
   */
  static bnbAnswersRangeForEnum(value: string): number[] {
    if (value.toLowerCase().includes('yes')) {
      return [4, 6, 8, 10];
    } else if (value.toLowerCase().includes('no')) {
      return [0, 1];
    } else if (value.toLowerCase().includes('partial')) {
      return [2, 2.5, 5, 7.5, 9];
    } else {
      return [];
    }
  }

  /**
   * Function to get the score against the given option selected for BNB
   * @param value - option selected
   * @returns - score against that option
   */
  static bnbAnswerEnumToNum(value: string): number {
    switch (value) {
      case BnbAnswerEnum.NO:
        return 0;
      case BnbAnswerEnum.PARTIAL:
        return 2;
      case BnbAnswerEnum.YES_NOT_DOCUMENTED:
        return 4;
      case BnbAnswerEnum.YES_DOCUMENTED_1:
        return 6;
      case BnbAnswerEnum.YES_DOCUMENTED_2:
        return 8;
      case BnbAnswerEnum.YES_DOCUMENTED_3:
        return 10;
    }
  }

  static answerEnumToNum(value: string, partialValue?: number): number {
    value = value?.split(' ').join('_');
    switch (value) {
      case AnswerEnum.NOT_APPLICABLE:
        if (UtilsService.isMidMarket || UtilsService.isBnBCyberSite) return 0;
        return 999;
      case AnswerEnum.YES:
        return 10;
      case AnswerEnum.PARTIAL:
        return this.partialAnswersMapping(partialValue);
      case AnswerEnumExtended.PARTIAL_25:
        return 2.5;
      case AnswerEnumExtended.PARTIAL_50:
        return 5;
      case AnswerEnumExtended.PARTIAL_75:
        return 7.5;
      case AnswerEnumExtended.PARTIAL_90:
        return 9;
      case AnswerEnum.NO:
        if (UtilsService.isMidMarket || UtilsService.isBnBCyberSite) return 1;
        return 0;
    }
  }
  /**
   * function to convert framework name to framework key incase of certifications and documents.
   * @params the framework name
   * @returns the framework key.
   */
  static convertFrameworkNametoKey(inputString: string): string {
    const keyArr = inputString.split(' ');
    keyArr.pop();
    return keyArr.join('_');
  }

  static removeStringAfterHash(inputString) {
    const index = inputString.indexOf('#');
    if (index !== -1) {
      return inputString.substring(0, index);
    }
    return inputString;
  }

  static getFrameworkAnswers(frameworkName: string): any {
    let keyName = null;
    const keys = Object.keys(FRAMEWORKS_ANSWERS);
    const name = this.removeStringAfterHash(frameworkName);
    for (const key of keys) {
      if (key === name) {
        keyName = key;
      }
    }
    if (keyName) {
      return { ...FRAMEWORKS_ANSWERS[keyName] };
    }
    if (UtilsService.isBnB) {
      return { ...FRAMEWORKS_ANSWERS.BNB_FRAMEWORKS };
    }
    //* For MidMarket and CyberSite
    //* Separated the framework Answers for MidMarket and CyberSite
    if (UtilsService.isBnBCyberSite || UtilsService.isMidMarket) {
      return { ...FRAMEWORKS_ANSWERS.BEECHER_MIDMARKET_FRAMEWORKS };
    }
    return { ...FRAMEWORKS_ANSWERS.OTHERS_FRAMEWORKS };
  }

  //* function to generate the answer variants for the given base answer.
  static generateAnswerVariants(base): any {
    // Check if the input is one of the allowed answers
    return ALLOWED_ANSWERS[base] ?? null;
  }

  /**
   * Checks if the given object contains a key named 'PARTIAL'.
   *
   * @param obj - The object to check for the 'PARTIAL' key.
   * @returns `true` if the object contains the 'PARTIAL' key, otherwise `false`.
   */
  static includesPartialKey(obj: AnswerObject): boolean {
    for (const key in obj) {
      if (key === 'PARTIAL') {
        return true;
      }
    }
    return false;
  }

  /**
   * Finds the object with the smallest score from the given AnswerObject.
   *
   * @param obj - The AnswerObject containing items with scores.
   * @returns An object containing the name and score of the item with the smallest score, or null if the input object is empty.
   */
  static getSmallestScore(obj: AnswerObject): { name: string; score: number } | null {
    let smallestScore = Infinity;
    let result: { name: string; score: number } | null = null;

    for (const key in obj) {
      if (obj[key].score < smallestScore) {
        smallestScore = obj[key].score;
        result = { name: obj[key].name, score: obj[key].score };
      }
    }

    return result;
  }

  /**
   * Finds the object with the biggest score from the given AnswerObject.
   *
   * @param obj - The AnswerObject containing items with scores.
   * @returns An object containing the name and score of the item with the biggest score, or null if the input object is empty.
   */
  static getBiggestScore(obj: AnswerObject): { name: string; score: number } | null {
    let biggestScore = -Infinity;
    let result: { name: string; score: number } | null = null;

    for (const key in obj) {
      if (obj[key].score > biggestScore) {
        biggestScore = obj[key].score;
        result = { name: obj[key].name, score: obj[key].score };
      }
    }

    return result;
  }

  /**
   * Retrieves the smallest question option order from the given AnswerObject.
   *
   * @param obj - The AnswerObject containing question options.
   * @returns An object containing the name and score of the option with the smallest order, or null if no options are found.
   */
  static getSmallestQuestionOptionOrder(obj: AnswerObject): { name: string; score: number } | null {
    let smallestOrder = Infinity;
    let result: { name: string; score: number } | null = null;

    for (const key in obj) {
      if (obj[key].questionOptionOrder < smallestOrder) {
        smallestOrder = obj[key].questionOptionOrder;
        result = { name: obj[key].name, score: obj[key].score };
      }
    }

    return result;
  }

  /**
   * Retrieves the type of framework answers based on the provided framework name.
   *
   * @param frameworkName - The name of the framework to look up.
   * @returns The key name of the framework answers if found, otherwise `null`.
   */
  static getFrameworkAnswersType(frameworkName: string): string {
    let keyName = null;
    const keys = Object.keys(FRAMEWORKS_ANSWERS);
    const name = this.removeStringAfterHash(frameworkName);
    for (const key of keys) {
      if (key === name) {
        keyName = key;
      }
    }
    return keyName;
  }

  /**
   * Calculates the average score for a given question object based on the framework name.
   *
   * @param currentQuestion - The current question object containing answers.
   * @param currFrameworkName - The name of the framework to use for calculations.
   * @returns An object containing the average score and the corresponding answer name.
   */
  static calculateAverageObject(currentQuestion: any, currFrameworkName: string): any {
    //* Only those answers will be used for average calculations that are not from Task Owner
    currentQuestion.answers = currentQuestion.answers.filter(answer => !answer?.isFromTaskOwner);
    const question = currentQuestion;
    const averageScore = { answer: null, answerName: null };
    //* Get the answers for the given framework
    const frameworkAnswers = UtilsService.getFrameworkAnswers(currFrameworkName);
    //* Get the type of answers for the given framework
    const answersType = UtilsService.getFrameworkAnswersType(currFrameworkName);

    //* Calculate the sum of scores for the given answers
    const calculateSumOfScores = (answers: any[]) => {
      return answers.reduce((accumulator, currentAnswer) => {
        return !NA_SCORES.includes(currentAnswer.answer) ? accumulator + currentAnswer.answer : accumulator;
      }, 0);
    };

    //* Filter out the answers that are not applicable
    const filterApplicableAnswers = (answers: any[]) => {
      return answers.filter(answer => !NA_SCORES.includes(answer.answer));
    };

    //* For Normal Frameworks that have regular answers
    if (!answersType) {
      //* Calculate the sum of scores for the given answers
      const sumOfScores = calculateSumOfScores(question.answers);
      //* Filter out the answers that are other than Not Applicable
      const filteredAnswers = filterApplicableAnswers(question.answers) || [];

      const totalAssignedUsers = currentQuestion.assignmentCount;

      //* Count users who answered "Not Applicable"
      const totalNaUsers = question.answers.filter(answer => NA_SCORES.includes(answer.answer)).length;
      //* Calculate the adjusted denominator
      const adjustedTotalUsers = totalAssignedUsers - totalNaUsers;
      let averageOfScore = 0;

      if (adjustedTotalUsers <= 0 || filteredAnswers?.length === 0) {
        averageOfScore = 999;
      } else {
        //* Calculate the average score
        averageOfScore = sumOfScores / adjustedTotalUsers;
      }

      averageScore.answer = averageOfScore;
      //* Convert the average score to an answer name
      averageScore.answerName = UtilsService.answerNumToEnum(averageOfScore);
      return averageScore;
    } else {
      //* For frameworks that have extended answers and different Answers

      //* Check if all answers are the same
      const firstAnswerName = question.answers[0].answerName;
      const firstAnswer = question.answers[0].answer;

      const totalAssignedUsers = currentQuestion.assignmentCount;
      const totalAnswers = question.answers.length;
      //* Count users who answered "Not Applicable"
      const totalNaUsers = question.answers.filter(answer => NA_SCORES.includes(answer.answer)).length;
      //* Calculate the adjusted denominator
      const adjustedTotalUsers = totalAssignedUsers - totalNaUsers;

      if (
        totalAssignedUsers === totalAnswers &&
        question.answers.every(answer => answer.answerName === firstAnswerName)
      ) {
        averageScore.answer = firstAnswer;
        averageScore.answerName = firstAnswerName;
        return averageScore;
      }

      //* Get the smallest and biggest scores for the given answers
      const minScore = UtilsService.getSmallestScore(frameworkAnswers);
      const maxScore = UtilsService.getBiggestScore(frameworkAnswers);
      //* Get the smallest order for the given answers
      const smallestOrder = UtilsService.getSmallestQuestionOptionOrder(frameworkAnswers);
      //* Check if the answers include a partial key
      const isPartial = UtilsService.includesPartialKey(frameworkAnswers);

      //* Calculate the sum of scores for the given answers
      const sumOfScores = calculateSumOfScores(question.answers);
      //* Filter out the answers that are other than not applicable
      const filteredAnswers = filterApplicableAnswers(question.answers) || [];
      let averageOfScore = 0;
      //* if there are no applicable answers, set the average score to the max score i.e 999
      if (adjustedTotalUsers <= 0 || filteredAnswers?.length === 0) {
        averageScore.answer = maxScore.score;
        averageScore.answerName = maxScore.name;
        return averageScore;
      } else {
        //* Calculate the average score
        averageOfScore = sumOfScores / adjustedTotalUsers;
      }

      //* if the average is mix of different Yes answers
      if (averageOfScore === 10) {
        //* if the average is 10, return the max score
        averageScore.answer = smallestOrder.score;
        averageScore.answerName = smallestOrder.name;
        return averageScore;
      }

      //* if the average is mix of different answers
      if (averageOfScore >= 0 && averageOfScore < 10) {
        //* if framework has partial key
        if (isPartial) {
          averageScore.answer = averageOfScore;
          averageScore.answerName = 'PARTIAL';
          return averageScore;
        } else {
          //* else return the min score i.e no or Not in place
          averageScore.answer = minScore.score;
          averageScore.answerName = minScore.name;
          return averageScore;
        }
      }
      return averageScore;
    }
  }

  /**
   * Retrieves the most recent answer from the task owner, if any.
   *
   * @param answers - An array of answer objects to search through
   * @returns The most recent answer object from a task owner, or null if no task owner answer exists
   */
  static getTaskOwnerAnswer(answers: any[]): any {
    if (!answers || answers.length === 0) {
      return null;
    }
    answers.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    const isTaskOwnerAnswer = answers[0]?.isFromTaskOwner || false;
    //* if the latest answer is from task owner return that answer
    if (isTaskOwnerAnswer) {
      return answers[0];
    }
    return null;
  }

  static formatAnswerValue(answer): string {
    if (answer) {
      return answer.toUpperCase().split(' ').join('_');
    }
  }

  static assignObjectArrayProperty(prev, current, property): void {
    if (prev[property]) {
      prev[property].push(...current[property]);
      prev[property] = [...new Set(prev[property])];
    } else {
      prev[property] = current[property];
    }
  }

  //* Function to get the weight of the question based on its impact
  static getQuestionWeight(impact: string): number {
    if (!impact) {
      return 0;
    }

    const impactLower = impact.toLowerCase();
    const weightMap = {
      [QuestionImpactsEnum.CRITICAL.name.toLowerCase()]: QuestionImpactsEnum.CRITICAL.weight,
      [QuestionImpactsEnum.HIGH.name.toLowerCase()]: QuestionImpactsEnum.HIGH.weight,
      [QuestionImpactsEnum.MEDIUM.name.toLowerCase()]: QuestionImpactsEnum.MEDIUM.weight,
      [QuestionImpactsEnum.LOW.name.toLowerCase()]: QuestionImpactsEnum.LOW.weight,
    };

    return weightMap[impactLower] || 0;
  }

  static initImpact(question): string {
    return question.impact ? question.impact : ImpactTextEnum.LOW;
  }
  static getIndexFromArray(questions, id: string): number {
    return questions.findIndex(question => {
      return question.id === id;
    });
  }

  static checkNewEntity(newSubList, existingSubList): GetEntityQuery {
    if (UtilsService.isDefined(newSubList)) {
      if (UtilsService.isDefined(existingSubList)) {
        if (existingSubList.length < newSubList.length) {
          const newlyCreatedEntity = newSubList.filter(
            ({ name: newSubName }) =>
              !existingSubList.some(({ name: existingSubName }) => existingSubName === newSubName)
          );

          return this.isDefined(newlyCreatedEntity) && newlyCreatedEntity.length !== 0 ? newlyCreatedEntity[0] : null;
        }
      }
    }
    return null;
  }
  static getCurrentDatePlus60Days(): NgbDateStruct {
    const currentDate = new Date();
    const futureDate = new Date(currentDate);

    futureDate.setDate(currentDate.getDate() + 60); // Add 60 days to the current date

    return {
      year: futureDate.getFullYear(),
      month: futureDate.getMonth() + 1,
      day: futureDate.getDate(),
    };
  }
  static getDateInNgbDateStructFormat(date): NgbDateStruct {
    if (typeof date === 'string' || typeof date === 'number') {
      date = new Date(date);
      return {
        day: date.getDate(),
        month: date.getMonth() + 1,
        year: date.getFullYear(),
      };
    } else if (typeof date === 'object') {
      return {
        day: date.getDate(),
        month: date.getMonth() + 1,
        year: date.getFullYear(),
      };
    } else {
      return {
        day: 0,
        month: 0,
        year: 0,
      };
    }
  }
  static formatDateString(timestamp: number, isDayFirst: boolean = false): string {
    const date = new Date(timestamp);

    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    if (isDayFirst) {
      return `${day}/${month}/${year}`;
    }
    return `${month}/${day}/${year}`;
  }

  // Write helper function which gets timestamp and returns the date in the format of October 10, 2025
  static formatDateFromTimestamp(timestamp: number): string {
    const date = new Date(timestamp);
    const res = date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    console.log('res', res);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }

  static formatDateFromNgbDateStruct(dateStruct: NgbDateStruct): NgbDateStruct {
    const day = dateStruct.day;
    const month = dateStruct.month;
    const year = dateStruct.year % 100;

    return {
      day,
      month,
      year,
    };
  }
  static ngbDateStructToDate(date: NgbDateStruct): Date {
    return new Date(date.year, date.month - 1, date.day);
  }

  // Convert date string in DD.MM.YY format to Date object
  static parseDateString(dateString: string): Date {
    const [day, month, year] = dateString.split('.').map(Number);
    const fullYear = year < 100 ? year + 2000 : year;
    return new Date(fullYear, month - 1, day); // Month is 0-indexed
  }

  // Add days to a date string in DD.MM.YY format and return the new date in DD.MM.YY format
  static addDaysToDate(dateString: string, daysToAdd: number): string {
    const date = this.parseDateString(dateString);
    date.setDate(date.getDate() + daysToAdd);
    const newDay = String(date.getDate()).padStart(2, '0');
    const newMonth = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
    const newYear = String(date.getFullYear()).slice(-2);
    return `${newDay}.${newMonth}.${newYear}`;
  }
  static addDaysToDateInt(nextStartingDate, numberOfDays) {
    // Destructure the day, month, and year from the nextStartingDate object
    const { day, month, year } = nextStartingDate;

    // Create a Date object from the provided day, month, and year
    const date = new Date(year + 2000, month - 1, day); // Assuming the year is 20xx

    // Add the specified number of days
    date.setDate(date.getDate() + numberOfDays);

    // Convert the date to an AWS timestamp (Unix timestamp in milliseconds)
    const nextReviewDate = date.getTime();

    return nextReviewDate;
  }
  static dateDifferenceInDays(endDate: NgbDateStruct, startDate: NgbDateStruct): number {
    const end = new Date(endDate.year, endDate.month - 1, endDate.day); // Convert NgbDateStruct to Date
    const start = new Date(startDate.year, startDate.month - 1, startDate.day); // Convert NgbDateStruct to Date
    const differenceInTime = end.getTime() - start.getTime(); // Difference in milliseconds
    return Math.floor(differenceInTime / (1000 * 3600 * 24)); // Convert milliseconds to days
  }
  /**
   * This returns true if two object are equal(contains same keys)
   * @param o1 object 1
   * @param o2 object 2
   */
  static isObjectEqual(o1: any, o2: any) {
    for (const p in o1) {
      if (Object.prototype.hasOwnProperty.call(o1, p)) {
        if (o1[p] !== o2[p]) {
          return false;
        }
      }
    }
    for (const p in o2) {
      if (Object.prototype.hasOwnProperty.call(o2, p)) {
        if (o1[p] !== o2[p]) {
          return false;
        }
      }
    }
    return true;
  }

  /**
   * return days in a months
   * @param month month number
   * @param year year in number
   */
  static getMonthDays = (month: number, year: number): number => new Date(year, month, 0).getDate();

  static getDateFromNgbDateStruct(date: NgbDateStruct): Date {
    return new Date(date.year, date.month, date.day);
  }
  static getDateFromYearMonthDate(year: number, month: number, day: number): Date {
    return new Date(year, month, day);
  }
  static findIndexBasedOnKey(arr, key, value): number {
    return arr.findIndex(element => element[key] === value);
  }

  static capitalizeText(text: string): string {
    return text
      .split(' ')
      .map(t => t.charAt(0).toUpperCase() + t.slice(1))
      .join(' ');
  }

  static getLabelValue(value: number, labelType: string): string {
    if (value <= 25) {
      return ProbabilityEnum.LOW;
    } else if (value <= 50) {
      return ProbabilityEnum.MEDIUM;
    } else if (value <= 75) {
      return ProbabilityEnum.HIGH;
    } else if (value <= 100) {
      if (labelType === RiskMapEnum.IMPACT.toLowerCase()) {
        return ImpactEnum.CRITICAL;
      } else {
        return ProbabilityEnum.CRITICAL;
      }
    }
    return '';
  }
  static getLabelValueNum(value: string): number {
    const val = value.toLowerCase();
    switch (val) {
      case 'low':
        return 2.5;
      case 'medium':
        return 5;
      case 'high':
        return 7.5;
      case 'critical':
        return 10;
      default:
        return 0;
    }
  }

  static getProbabilityEnumValue(value: number): RiskProbabilityEnum {
    switch (true) {
      case value <= 2.5:
        return RiskProbabilityEnum.LOW;
      case value <= 5:
        return RiskProbabilityEnum.MEDIUM;
      case value <= 7.5:
        return RiskProbabilityEnum.HIGH;
      case value <= 10:
        return RiskProbabilityEnum.CRITICAL;
    }
  }

  static getImpactEnumValue(value: number): RiskImpactEnum {
    switch (true) {
      case value <= 2.5:
        return RiskImpactEnum.LOW;
      case value <= 5:
        return RiskImpactEnum.MEDIUM;
      case value <= 7.5:
        return RiskImpactEnum.HIGH;
      case value <= 10:
        return RiskImpactEnum.CRITICAL;
    }
  }

  static getLabelValueBaseOnNum(num: number, seriesType = 1): string {
    switch (num) {
      case 2.5:
        return 'low';
      case 5:
        return 'medium';
      case 7.5:
        return 'high';
      case 10: {
        if (seriesType === 1) {
          return 'critical';
        }
        if (seriesType === 2) {
          return 'critical';
        }
        break;
      }
      default: {
        return '';
      }
    }
  }

  static getRoleMapped(role = 'ADMIN', allRoles = []) {
    const roleMapper = {
      ADMIN: [...allRoles],
      MSSP: [...allRoles].filter(i => i.toLowerCase() !== RoleEnum.ADMIN.toLowerCase()),
      'Entity Leader': [...allRoles].filter(
        i => i.toLowerCase() !== RoleEnum.ADMIN.toLowerCase() && i.toLowerCase() !== RoleEnum.MSSP.toLowerCase()
      ),
      'Sub Entity Leader': [...allRoles].filter(
        i =>
          i.toLowerCase() !== RoleEnum.ADMIN.toLowerCase() &&
          i.toLowerCase() !== RoleEnum.MSSP.toLowerCase() &&
          i.toLowerCase() !== 'entity leader'
      ),
      PARTICIPANT: [...allRoles].filter(i => i.toLowerCase() === RoleEnum.PARTICIPANT),
    };
    return roleMapper[role];
  }
  static getRandomEnumVal(enumList: any): any {
    const rand = Math.floor(Math.random() * Object.keys(enumList).length);
    return enumList[Object.keys(enumList)[rand]];
  }

  // filter uniq value from array
  static uniqueByKey(arr, key) {
    const flag = {};
    const unique = [];
    arr.forEach(elem => {
      if (!flag[elem[key]]) {
        flag[elem[key]] = true;
        unique.push(elem);
      }
    });
    console.log('Duplicates', flag);
    return unique;
  }

  /**
   *
   * @param pillarScore is type of number and contains the score of pillar
   * @param tierIndex  is the Index of tier and its values will be ( Tiers 1,2,3,4)
   * @returns type of number that contains the total calculated Score
   */
  static calculatePillarsScoreByTier(pillarScore: any, tierIndex: number): number {
    const dividerValuesPerLevel = [2.5, 5, 7.5, 10];
    const totalScore = pillarScore ? pillarScore.toFixed(1) : 0;
    const score =
      totalScore <= dividerValuesPerLevel[tierIndex - 1]
        ? +((totalScore / dividerValuesPerLevel[tierIndex - 1]) * 10).toFixed(1)
        : 10;
    return score;
  }

  static fixBoundaries(value: number): number {
    value = value < 0 ? 0 : value > 10 ? 10 : value;
    return value;
  }

  static calculateMaturityScore(total: number): number {
    if (!this.isDefined(total)) {
      return 1;
    }

    total = +total.toFixed(1);

    const BB_RANGE = [
      { range: 0.0, value: 0 },
      { range: 1.0, value: 1 },
      { range: 2.5, value: 2 },
      { range: 5.0, value: 3 },
      { range: 7.5, value: 4 },
      { range: 10.0, value: 5 },
    ];

    const RANGE = [
      { range: 0.0, value: 0 },
      { range: 1.0, value: 1 },
      { range: 2.5, value: 2 },
      { range: 5.0, value: 3 },
      { range: 7.5, value: 4 },
      { range: 10.0, value: 5 },
      { range: 999.0, value: 0 },
    ];

    const range = this.isBnB || this.isMidMarket || this.isBnBCyberSite ? BB_RANGE : RANGE;
    const maturity = range.find(item => total <= item.range);

    return maturity ? maturity.value : 1;
  }

  static partialAnswersMapping(value: number, sliderToVal = true): number {
    let sliderVal = 0;
    if (!sliderToVal) {
      // here we're handling values except 2.5, 5, 7.5 as well, because sometimes answer value
      // falls in between in the third party integrations case.
      if (value === 2.5 || (value > 0 && value < 4)) {
        sliderVal = 1;
      } else if (value === 5 || (value >= 4 && value < 6)) {
        sliderVal = 2;
      } else if (value === 7.5 || (value >= 6 && value < 9)) {
        sliderVal = 3;
      } else if (value === 9 || (value >= 9 && value < 10)) {
        sliderVal = 4;
      } else {
        sliderVal = -1;
      }
    } else {
      switch (value) {
        case 1:
          sliderVal = 2.5;
          break;
        case 2:
          sliderVal = 5;
          break;
        case 3:
          sliderVal = 7.5;
          break;
        case 4:
          sliderVal = 9;
          break;
        default:
          sliderVal = -1;
          break;
      }
    }
    return sliderVal;
  }
  /**
   * This is just a quick fix as the issue resides in Create Framework Lambda
   * We are storing Control Labels in Question Labels, this is only a fix for
   * the Hi-Trust Framework
   * @param orderNum The order number of the question
   * @returns Name of the label according to the question order number
   */
  static hiTrustLabelName(orderNum: number): string {
    let numName = orderNum % 10;
    numName = numName === 0 ? 10 : numName;
    numName = numName > 5 ? numName - 5 : numName;
    switch (numName) {
      case 5:
        return 'Policy';
      case 1:
        return 'Process';
      case 2:
        return 'Implemented';
      case 3:
        return 'Measured';
      case 4:
        return 'Managed';
      default:
        return 'Policy';
    }
  }

  // list should be array of value not objects
  static hasEmptyValue(list: any[]): boolean {
    return list.indexOf('') !== -1 || list.indexOf(null) !== -1 || list.indexOf(undefined) !== -1;
  }

  static toLowerCaseAll(list: string[]): string[] {
    return list.join(',').toLowerCase().split(',');
  }

  // removes empty spaces from string array
  static removeEmptySpacesFromList(list: string[]): string[] {
    return list.map(element => {
      return element.trim();
    });
  }

  static getTimeDifference(fromDate: any, toDate?: any): number {
    return (Math.abs(new Date(fromDate).getTime() - new Date(toDate).getTime()) / (1000 * 60)) % 60;
  }

  static getSanitizedDomain(dom: string): string {
    const { domain, topLevelDomains } = parseDomain(fromUrl(dom)) as any;
    let sanitizedDomain = '';
    if (topLevelDomains?.length) {
      sanitizedDomain = `${domain}.${topLevelDomains.join('.')}`;
    } else {
      sanitizedDomain = `${domain}.${topLevelDomains}`;
    }
    return sanitizedDomain;
  }

  // validating domains
  static validateDomain(domain: string): boolean {
    let isDomainValid = true;
    const sanitizedDomain = this.getSanitizedDomain(domain);
    if (sanitizedDomain.includes('undefined') || !validator.isURL(domain, { require_valid_protocol: false })) {
      isDomainValid = false;
    }
    return isDomainValid;
  }

  static removeSpecialCharactersFromString(item: string): string {
    let res = '';
    // eslint-disable-next-line no-useless-escape
    const regex = /[ `!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/;

    for (let i = 0; i < item.length; i++) {
      if (!regex.test(item.charAt(i))) {
        res = res.concat(item.charAt(i));
      }
    }
    return res;
  }

  static getImpactEnum(value: number): APIImpactEnum {
    if (value >= 0 && value < 25) {
      return APIImpactEnum.LOW;
    } else if (value >= 25 && value < 50) {
      return APIImpactEnum.MEDIUM;
    } else if (value >= 50 && value < 75) {
      return APIImpactEnum.HIGH;
    } else if (value >= 75 && value <= 100) {
      return APIImpactEnum.CRITICAL;
    }
    return null;
  }

  static noSpacesTest(str: string): boolean {
    const noSpaces = RegExp(/^[\s]*$/); // regular expression to check for consecutive empty spaces
    return noSpaces.test(str);
  }

  static dateIsValid(d: string): boolean {
    const date = new Date(d);
    return date instanceof Date && !isNaN(date.getTime());
  }

  static getEnumKey(enumClass: any, value: string | number): string {
    const enumValues = Object.values(enumClass);
    const index = enumValues.findIndex(enumVal => String(enumVal).toLowerCase() === String(value).toLowerCase());

    return index !== -1 ? Object.keys(enumClass)[index] : null;
  }

  /**
   *  calculate the score and avg out.
    @param {} data1 an object that contains {isActive:boolean , score:float ,weight:Int }
    @param {} data2 an object that contains {isActive:boolean , score:float ,weight:Int}
   * @returns
   */
  static calculateWeightedAvg(data1: any, data2: any): number {
    try {
      if (data1 && data2) {
        let totalAccumulative = 0;
        let accumulativeSum1 = data1.isActive ? data1.score : 0;
        let accumulativeSum2 = data2.isActive ? data2.score : 0;
        let divider = (data1.isActive ? 1 : 0) + (data2.isActive ? 1 : 0);
        // Weights are only applied when we have
        if (data1?.isActive && data2?.isActive && data1?.weight && data2?.weight) {
          accumulativeSum1 = accumulativeSum1 * data1?.weight;
          accumulativeSum2 = accumulativeSum2 * data2?.weight;
          divider = 100;
        }
        totalAccumulative = accumulativeSum1 + accumulativeSum2;
        return divider > 0 ? totalAccumulative / divider : 10;
      }
    } catch (e) {
      console.log('Error : ', e);
    }
    return 0;
  }

  /**
   * For BNB only - converts the total
   * score to be calculated out of 5
   */
  static totalScoreOf5(score): any {
    if (!UtilsService.isDefined(score)) {
      return (0.0).toFixed(1);
    }

    let numb = parseFloat(score);
    const isString = typeof score === 'string';
    if (UtilsService.isBnB) {
      numb = numb / 2;
    }
    return isString ? numb.toFixed(1) : numb === 0 ? (0.0).toFixed(1) : parseFloat(numb.toFixed(1));
  }

  //* function converts NA filter to N/A used in remediation filters
  static updateNAFilter(arr: any): any {
    return arr.map(value => (value === 'NA' ? 'N/A' : value));
  }
  static getFullFrameworkKey(frameKey: string, level: string): string {
    const treeName = frameKey.split(' ').join('_') + '_' + level.split(' ').join('_');
    return treeName;
  }

  /**
   * *Function splits and returns framework name
   * @param frameworkName framework name
   * @returns framework name without hash #
   */
  static extractFrameworkName(frameworkName: string): string | null {
    if (!frameworkName) {
      return null;
    }

    const [name] = frameworkName.split('#');
    return name || null;
  }

  /**
   * *Function check the html tags in given input and returns true/false
   * @param Input Input string
   * @returns true if there is any html tag else false will return
   */
  static checkForHtml(input: string): boolean {
    const htmlTagPattern = /<\/?[a-z][\s\S]*>/i;
    return htmlTagPattern.test(input);
  }

  static getFrameworkNameByKey(key) {
    return key.replace(/_/g, ' ');
  }
}

export function calculateFrameworkAverageScore(subNodeName: string): FrameworkNode {
  const subNodes = this.frameworkList.filter(subNode => subNode?.name === subNodeName);
  const singleNode: FrameworkNode = subNodes.reduce(UtilsService.calculateNodesScoreSum, {
    key: '',
    name: '',
    scores: { total: 0.0, target: 0.0, collection: 0.0 },
  });
  if (singleNode) {
    singleNode.scores.total = singleNode.scores.total / subNodes.length;
    singleNode.scores.target = singleNode.scores.target / subNodes.length;
    singleNode.scores.collection = singleNode.scores.collection / subNodes.length;
    return singleNode;
  }
}
